/**
 * Scoring utilities for the artifact scanner
 */

export interface ScoreLabel {
  label: string
  color: string
  textColor: string
  description: string
}

/**
 * Get human-readable label for overall score
 */
export function getScoreLabel(score: number): ScoreLabel {
  if (score >= 80) {
    return {
      label: 'Release Ready',
      color: 'emerald',
      textColor: 'text-emerald-400',
      description: 'Your track is polished and ready for release!',
    }
  }
  if (score >= 60) {
    return {
      label: 'Needs Polish',
      color: 'yellow',
      textColor: 'text-yellow-400',
      description: 'Some minor issues that could be improved with mastering.',
    }
  }
  if (score >= 40) {
    return {
      label: 'Needs Work',
      color: 'orange',
      textColor: 'text-orange-400',
      description: 'Several issues detected that should be addressed.',
    }
  }
  return {
    label: 'Major Issues',
    color: 'red',
    textColor: 'text-red-400',
    description: 'Significant artifacts detected. Professional mastering recommended.',
  }
}

/**
 * Get severity level for individual metrics
 */
export type Severity = 'good' | 'moderate' | 'high' | 'critical'

export interface MetricSeverity {
  severity: Severity
  label: string
  color: string
}

/**
 * Get severity for sizzle score (lower is better)
 */
export function getSizzleSeverity(score: number): MetricSeverity {
  if (score < 0.2) {
    return { severity: 'good', label: 'Minimal', color: 'text-emerald-400' }
  }
  if (score < 0.4) {
    return { severity: 'moderate', label: 'Moderate', color: 'text-yellow-400' }
  }
  if (score < 0.6) {
    return { severity: 'high', label: 'High', color: 'text-orange-400' }
  }
  return { severity: 'critical', label: 'Severe', color: 'text-red-400' }
}

/**
 * Get severity for muddiness score (lower is better)
 */
export function getMuddinessSeverity(score: number): MetricSeverity {
  if (score < 0.2) {
    return { severity: 'good', label: 'Clean', color: 'text-emerald-400' }
  }
  if (score < 0.4) {
    return { severity: 'moderate', label: 'Some buildup', color: 'text-yellow-400' }
  }
  if (score < 0.6) {
    return { severity: 'high', label: 'Muddy', color: 'text-orange-400' }
  }
  return { severity: 'critical', label: 'Very muddy', color: 'text-red-400' }
}

/**
 * Get severity for click count (lower is better)
 */
export function getClickSeverity(count: number): MetricSeverity {
  if (count === 0) {
    return { severity: 'good', label: 'None detected', color: 'text-emerald-400' }
  }
  if (count <= 5) {
    return { severity: 'moderate', label: `${count} detected`, color: 'text-yellow-400' }
  }
  if (count <= 15) {
    return { severity: 'high', label: `${count} detected`, color: 'text-orange-400' }
  }
  return { severity: 'critical', label: `${count} detected`, color: 'text-red-400' }
}

/**
 * Get severity for dynamic range (higher is better)
 */
export function getDynamicRangeSeverity(db: number): MetricSeverity {
  if (db >= 10) {
    return { severity: 'good', label: 'Good dynamics', color: 'text-emerald-400' }
  }
  if (db >= 6) {
    return { severity: 'moderate', label: 'Moderate', color: 'text-yellow-400' }
  }
  if (db >= 4) {
    return { severity: 'high', label: 'Compressed', color: 'text-orange-400' }
  }
  return { severity: 'critical', label: 'Over-compressed', color: 'text-red-400' }
}

/**
 * Get severity for LUFS (closer to -14 is better)
 */
export function getLufsSeverity(lufs: number): MetricSeverity {
  const diff = Math.abs(lufs + 14)

  if (diff <= 2) {
    return { severity: 'good', label: 'Optimal', color: 'text-emerald-400' }
  }
  if (diff <= 4) {
    return { severity: 'moderate', label: lufs > -14 ? 'Slightly loud' : 'Slightly quiet', color: 'text-yellow-400' }
  }
  if (diff <= 8) {
    return { severity: 'high', label: lufs > -14 ? 'Too loud' : 'Too quiet', color: 'text-orange-400' }
  }
  return { severity: 'critical', label: lufs > -14 ? 'Way too loud' : 'Very quiet', color: 'text-red-400' }
}

/**
 * Format duration in MM:SS format
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
